import React, { useEffect, useState } from 'react';
import { 
  Container, 
  Typography, 
  Box, 
  Link,
  Grid, 
  Card, 
  CardContent,
  Fab,
  Tooltip
} from '@mui/material';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import backgroundImage from '../assets/Background.jpg';
import FeedbackForm from './common/FeedbackForm';
import FeedbackIcon from '@mui/icons-material/Feedback';

const DEFAULT_CONTACT_URL = 'https://www.vertilocity.com';

const Home = () => {
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const [feedbackOpen, setFeedbackOpen] = useState(false);

  useEffect(() => {
    // If the user is authenticated, redirect to the dashboard
    if (isAuthenticated) {
      navigate('/dashboard');
    }
  }, [isAuthenticated, navigate]);

  const handleOpenFeedback = () => {
    setFeedbackOpen(true);
  };

  const handleCloseFeedback = () => {
    setFeedbackOpen(false);
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        paddingTop: '32px',
        position: 'relative',
        backgroundColor: '#f8f9fa',
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundImage: `url(${backgroundImage})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          opacity: 0.08,
          zIndex: 0
        }
      }}
    >
      <Container maxWidth="lg">
        <Box sx={{ 
          py: 8,
          position: 'relative',
          zIndex: 1
        }}>
          {/* Hero Section */}
          <Box sx={{ 
            textAlign: 'center',
            mb: 6,
            position: 'relative'
          }}>
            <Typography 
              variant="h2" 
              component="h1" 
              sx={{ 
                fontWeight: 900,
                fontSize: { xs: '2rem', md: '3rem' },
                background: 'linear-gradient(45deg, #1a237e, #0d47a1)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                mb: 2,
                letterSpacing: '-0.5px'
              }}
            >
              Secure Your SharePoint.<br/>
              Elevate Your Insights.
            </Typography>
            <Typography 
              variant="h4" 
              component="h2" 
              sx={{ 
                mb: 2,
                fontWeight: 700,
                fontSize: { xs: '1.5rem', md: '1.75rem' },
                color: '#e65100',
                letterSpacing: '-0.25px'
              }}
            >
              SecurePoint IQ: Your needed solution for SharePoint 365 security<br/>
              and business intelligence information.
            </Typography>
            <Typography 
              variant="h6" 
              sx={{ 
                mb: 4,
                color: '#546e7a',
                maxWidth: '800px',
                margin: '0 auto',
                lineHeight: 1.6,
                fontSize: '1rem'
              }}
            >
              Take control of your SharePoint environment with powerful security and analytics tools.
            </Typography>
            <Typography variant="body1" sx={{ color: '#546e7a' }}>
              <Link
                href={process.env.REACT_APP_CONTACT_URL || DEFAULT_CONTACT_URL}
                target="_blank"
                rel="noopener noreferrer"
                sx={{
                  color: '#e65100',
                  fontWeight: 700,
                  fontSize: '1.1rem',
                  textDecoration: 'underline',
                  '&:hover': { color: '#bf360c' }
                }}
              >
                Contact Vertilocity to learn more
              </Link>
            </Typography>
          </Box>

          {/* Key Features Section */}
          <Box sx={{ mb: 6 }}>
            <Typography 
              variant="h3" 
              component="h3" 
              sx={{ 
                mb: 4,
                textAlign: 'center',
                fontWeight: 800,
                color: '#263238',
                fontSize: { xs: '1.75rem', md: '2rem' }
              }}
            >
              Key Features
            </Typography>
            <Grid container spacing={3}>
              {[
                {
                  title: 'Advanced Security Audits',
                  content: [
                    'Uncover access vulnerabilities and ensure compliance with ease',
                    'Generate comprehensive permission reports to safeguard your data'
                  ]
                },
                {
                  title: 'External Sharing Analysis',
                  content: [
                    'Track all externally shared content with detailed risk assessment',
                    'Identify and remediate potential data leakage risks'
                  ]
                },
                {
                  title: 'Usage Intelligence',
                  content: [
                    'Track file access and usage patterns across your organization',
                    'Understand collaboration trends and optimize content strategy'
                  ]
                },
                {
                  title: 'Storage Optimization',
                  content: [
                    'Monitor storage growth and identify opportunities for cost reduction',
                    'Plan capacity needs based on historical usage trends'
                  ]
                }
              ].map((feature, index) => (
                <Grid item xs={12} sm={6} md={3} key={index}>
                  <Card 
                    elevation={2}
                    sx={{ 
                      height: '100%',
                      display: 'flex',
                      flexDirection: 'column',
                      transition: 'transform 0.2s ease-in-out',
                      '&:hover': {
                        transform: 'translateY(-5px)'
                      },
                      borderRadius: 2
                    }}
                  >
                    <CardContent>
                      <Typography 
                        variant="h5" 
                        component="h3" 
                        gutterBottom
                        sx={{ 
                          fontWeight: 700,
                          color: '#0d47a1',
                          fontSize: '1.25rem'
                        }}
                      >
                        {feature.title}
                      </Typography>
                      <Box component="ul" sx={{ pl: 2 }}>
                        {feature.content.map((item, idx) => (
                          <Typography 
                            component="li" 
                            key={idx}
                            sx={{ 
                              mb: 1,
                              color: '#546e7a'
                            }}
                          >
                            {item}
                          </Typography>
                        ))}
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </Box>

          {/* Call to Action */}
          <Box 
            sx={{ 
              textAlign: 'center',
              py: 6,
              px: 2,
              backgroundColor: 'rgba(13, 71, 161, 0.05)',
              borderRadius: 4,
              position: 'relative'
            }}
          >
            <Typography 
              variant="h4" 
              component="h3" 
              sx={{ 
                mb: 2,
                fontWeight: 800,
                color: '#0d47a1'
              }}
            >
              Ready to secure your SharePoint environment?
            </Typography>
            <Typography 
              variant="body1" 
              sx={{ 
                mb: 4,
                color: '#546e7a',
                maxWidth: '700px',
                margin: '0 auto'
              }}
            >
              Join the list of organizations that trust SecurePoint IQ for their SharePoint security and analytics reporting needs.
            </Typography>
            <Typography variant="body1" sx={{ color: '#546e7a' }}>
              <Link
                href={process.env.REACT_APP_CONTACT_URL || DEFAULT_CONTACT_URL}
                target="_blank"
                rel="noopener noreferrer"
                sx={{
                  color: '#e65100',
                  fontWeight: 700,
                  fontSize: '1.1rem',
                  textDecoration: 'underline',
                  '&:hover': { color: '#bf360c' }
                }}
              >
                Contact Vertilocity to learn more
              </Link>
            </Typography>
          </Box>

          {/* Feedback Button */}
          <Tooltip title="Share Feedback" placement="left">
            <Fab 
              color="primary" 
              aria-label="feedback" 
              onClick={handleOpenFeedback}
              sx={{ 
                position: 'fixed', 
                bottom: 24, 
                right: 24,
                zIndex: 1000
              }}
            >
              <FeedbackIcon />
            </Fab>
          </Tooltip>

          {/* Feedback Form Dialog */}
          <FeedbackForm open={feedbackOpen} onClose={handleCloseFeedback} />
        </Box>
      </Container>
    </Box>
  );
};

export default Home;